import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtDateLong, monthLabel } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { FESTIVAL_CATEGORY, FESTIVAL_FAITH } from "@/lib/meta";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ContentDialog, emptyContent } from "@/pages/ContentCalendar";
import { FestivalDetail } from "@/components/FestivalDetail";
import { Loader2, Search, CalendarHeart, AlertTriangle } from "lucide-react";

function monthOptionsFwd(count = 18) {
  const opts = []; const now = new Date();
  for (let i = 0; i < count; i++) { const d = new Date(now.getFullYear(), now.getMonth() + i, 1); opts.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); }
  return opts;
}

export default function SpecialDays() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [faith, setFaith] = useState("all");
  const [region, setRegion] = useState("all");
  const [month, setMonth] = useState("all");
  const [selected, setSelected] = useState(null);
  const [planning, setPlanning] = useState(null);

  const { data: facets = { categories: [], faiths: [], regions: [] } } = useQuery({ queryKey: ["festival-facets"], queryFn: async () => (await api.get("/festivals/facets")).data });

  const queryStr = useMemo(() => {
    const p = new URLSearchParams({ all_year: "true" });
    if (category !== "all") p.set("category", category);
    if (faith !== "all") p.set("faith", faith);
    if (region !== "all") p.set("region", region);
    if (month !== "all") p.set("month", month);
    if (search.trim()) p.set("q", search.trim());
    return p.toString();
  }, [category, faith, region, month, search]);

  const { data = [], isLoading } = useQuery({ queryKey: ["special-days", queryStr], queryFn: async () => (await api.get(`/festivals?${queryStr}`)).data });

  const focusId = params.get("festival");
  useEffect(() => {
    if (focusId && data.length) {
      const f = data.find((x) => x.id === focusId);
      if (f) { setSelected(f); setParams({}, { replace: true }); }
    }
  }, [focusId, data]); // eslint-disable-line

  const grouped = useMemo(() => {
    const g = {};
    data.forEach((f) => { const m = f.date.slice(0, 7); (g[m] = g[m] || []).push(f); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  const planInitial = planning ? { ...emptyContent(planning.date, ""), festival_id: planning.id, title: planning.name, content_type: "festival" } : null;

  return (
    <div className="space-y-6" data-testid="special-days-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl">
          <CalendarHeart className="h-6 w-6 text-rose-500" />Special Days & Festivals
        </h1>
        <p className="mt-1 text-sm text-zinc-500">Agency-wide content-opportunity calendar — Indian religious, regional, national, global, professional & marketing days.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search festivals & days…" className="pl-9" data-testid="festival-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-40" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{facets.categories.map((c) => <SelectItem key={c} value={c}>{FESTIVAL_CATEGORY[c]?.label || c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={faith} onValueChange={setFaith}>
          <SelectTrigger className="w-36" data-testid="filter-faith"><SelectValue placeholder="Faith" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Faiths</SelectItem>{facets.faiths.map((c) => <SelectItem key={c} value={c}>{FESTIVAL_FAITH[c]?.label || c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="w-40" data-testid="filter-region"><SelectValue placeholder="Region" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Regions</SelectItem>{facets.regions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-40" data-testid="filter-month"><SelectValue placeholder="Month" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Months</SelectItem>{monthOptionsFwd().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
      ) : data.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-200 py-16 text-center text-sm text-zinc-400">No special days match your filters.</div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([m, items]) => (
            <div key={m}>
              <div className="mb-2 flex items-center gap-2">
                <h2 className="font-heading text-sm font-semibold text-zinc-700">{monthLabel(m)}</h2>
                <Badge color="grey">{items.length}</Badge>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((f) => (
                  <button key={f.id} onClick={() => setSelected(f)} data-testid={`special-day-${f.id}`} className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-left shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50">
                    <span className="text-2xl">{f.emoji}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-semibold text-zinc-900">{f.name}</span>
                        {f.variable && <AlertTriangle className="h-3 w-3 shrink-0 text-amber-500" />}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5">
                        <span className="text-xs text-zinc-500">{fmtDateLong(f.date)}</span>
                        <Badge color={FESTIVAL_CATEGORY[f.category]?.color || "grey"} className="!px-1.5 !py-0 text-[10px]">{FESTIVAL_CATEGORY[f.category]?.label || f.category}</Badge>
                      </div>
                      {f.total_clients > 0 && (
                        <div className={`mt-0.5 text-[11px] font-medium ${f.planned_count < f.total_clients ? "text-amber-600" : "text-emerald-600"}`}>{f.planned_count}/{f.total_clients} clients planned</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <FestivalDetail fest={selected} onClose={() => setSelected(null)} onPlan={(f) => { setSelected(null); setPlanning(f); }} />
      <ContentDialog
        open={!!planning}
        onOpenChange={(o) => !o && setPlanning(null)}
        initial={planInitial}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["special-days"] }); qc.invalidateQueries({ queryKey: ["festivals"] }); setPlanning(null); }}
      />
    </div>
  );
}
